/**
 * One competency: what it requires, who it applies to, and — for an
 * administrator — who has been assigned it, who has done it, and the
 * evidence waiting on a signature.
 */

import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { hasAtLeast, ROLE_DISPLAY_NAMES, isRole } from '../../types/roles';
import { isCallFailure } from '../../lib/callable';
import { COMP_DUE_DAYS_MAX, COMP_REASON_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { formatDay } from '../../shared/dates';
import { useCompetencyAssignments, useCompetencyDetail, useCompetencyEvidence, useCompetencyAction, type CompetencyAction, type PublishResult } from './useCompetencies';
import {
  ASSIGNMENT_LABEL, ASSIGNMENT_TONE, competencyActions, COMPETENCY_TYPE_LABEL, DEFAULT_ATTESTATION, eventLabel, requirementsText,
  STATUS_LABEL, STATUS_TONE, VALIDATION_METHOD_LABEL, type Assignment, type Evidence,
} from './types';
import { dueText, sortAssignments } from './MyCompetenciesPage';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Competencies.module.css';

type Panel = 'publish' | 'retire' | null;

export function publishedMessage(r: PublishResult | undefined): string {
  if (r?.skipped) return 'Nothing changed since the last version, so no new version was published.';
  const n = r?.assignmentCount ?? 0;
  return `Published as version ${r?.versionNumber ?? ''}`.trim() + `. Assigned to ${n} ${n === 1 ? 'person' : 'people'}.`;
}

export function CompetencyDetailPage() {
  const { competencyId = '' } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  const admin = hasAtLeast(roles, 'admin');

  const detail = useCompetencyDetail(orgId, competencyId);
  const assignments = useCompetencyAssignments(orgId, competencyId, admin);
  const evidence = useCompetencyEvidence(orgId, competencyId, admin);
  const act = useCompetencyAction(orgId, competencyId);
  const departments = useTaxonomy('department', Boolean(orgId));
  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [departments.data]);

  const [panel, setPanel] = useState<Panel>(null);
  const [dueDays, setDueDays] = useState('30');
  const [dueDate, setDueDate] = useState('');
  const [reason, setReason] = useState('');
  const [waiving, setWaiving] = useState<string | null>(null);
  const [waiveReason, setWaiveReason] = useState('');
  const [deciding, setDeciding] = useState<string | null>(null);
  const [comments, setComments] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  function run(action: CompetencyAction, message: (r: PublishResult) => string) {
    setDone(null);
    act.mutate(action, {
      onSuccess: (r) => {
        setDone(message(r ?? {}));
        setPanel(null); setWaiving(null); setDeciding(null);
        setReason(''); setWaiveReason(''); setComments('');
      },
    });
  }

  if (detail.isLoading) return <Spinner label="Opening the competency" />;
  if (detail.error || !detail.data) {
    const notFound = isCallFailure(detail.error) && detail.error.code === 'not-found';
    return (
      <div className={styles.body}>
        <PageState title={notFound ? 'That competency is not here' : 'Could not open the competency'} action={{ to: '/competencies', label: 'Back to competencies' }} tone="alert">
          {notFound ? 'It may have been removed, or the link may be wrong.' : isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }

  const { competency: c, versions, events } = detail.data;
  const can = competencyActions(c.status, roles);
  const busy = act.isPending;
  const req = c.evidenceRequirements;
  const rows = sortAssignments(assignments.data ?? []);
  const evidenceByAssignment = new Map((evidence.data ?? []).map((e) => [e.assignmentId, e]));
  const pendingApproval = (evidence.data ?? []).filter((e) => req.managerApproval && e.managerApproval === null);
  const counts = rows.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] ?? 0) + 1 }), {} as Record<string, number>);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.titled}>
            <p className={styles.eyebrow}>Competency{c.currentVersionNumber ? ` · Version ${c.currentVersionNumber}` : ''}</p>
            <div className={styles.titleLine}>
              <BackLink to="/competencies" label="Back to competencies" />
              <h1 className={styles.title}>{c.title}</h1>
            </div>
            <p className={own.status}>
              <span className={`${styles.tag} ${styles[STATUS_TONE[c.status]]}`}>{STATUS_LABEL[c.status]}</span>
              <span>Evidence: {requirementsText(req)}</span>
            </p>
          </div>
          {(can.edit || can.publish || can.retire) && (
            <div className={styles.actions}>
              {can.edit && <Link to={`/competencies/${c.id}/edit`} className={styles.btn}>Edit</Link>}
              {can.publish && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'publish'} onClick={() => setPanel(panel === 'publish' ? null : 'publish')}>
                  {c.status === 'published' ? 'Publish new version…' : 'Publish…'}
                </button>
              )}
              {can.retire && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'retire'} onClick={() => setPanel(panel === 'retire' ? null : 'retire')}>Retire…</button>}
            </div>
          )}
        </div>

        {panel === 'publish' && (
          <form className={own.panel} aria-labelledby={id('pub-t')}
            onSubmit={(e: FormEvent) => { e.preventDefault(); run({ name: 'publishCompetency', dueDaysDefault: dueDays, dueDateOverride: dueDate }, publishedMessage); }}>
            <h2 className={own.panelTitle} id={id('pub-t')}>{c.status === 'published' ? 'Publish a new version' : 'Publish this competency'}</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              Everyone it applies to is assigned it with a due date. {c.status === 'published' && 'If nothing has changed since the current version, nothing is published.'}
            </p>
            <div className={form.row}>
              <div className={form.field}>
                <label className={form.label} htmlFor={id('days')}>Due in <span className={form.optional}>(days)</span></label>
                <input id={id('days')} className={form.input} type="number" min={1} max={COMP_DUE_DAYS_MAX} step={1} value={dueDays}
                  onChange={(e) => setDueDays(e.target.value)} disabled={Boolean(dueDate)} />
              </div>
              <div className={form.field}>
                <label className={form.label} htmlFor={id('date')}>Or due on <span className={form.optional}>(a date)</span></label>
                <input id={id('date')} className={form.input} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </div>
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Publishing…' : 'Publish and assign'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'retire' && (
          <form className={own.panel} aria-labelledby={id('ret-t')}
            onSubmit={(e: FormEvent) => { e.preventDefault(); run({ name: 'retireCompetency', reason }, () => 'Retired. Open assignments stay on record; no new ones are made.'); }}>
            <h2 className={own.panelTitle} id={id('ret-t')}>Retire this competency?</h2>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('reason')}>Reason <span className={form.optional}>(optional)</span></label>
              <textarea id={id('reason')} className={form.textarea} rows={3} maxLength={COMP_REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value.slice(0, COMP_REASON_MAX))} />
              <CharCount value={reason} max={COMP_REASON_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Retiring…' : 'Retire'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}
      </header>

      <div className={styles.body}>
        {done && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{done}</div>}
        {act.error && <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{isCallFailure(act.error) ? act.error.message : 'That could not be done.'}</div>}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Details</h2>
          <dl className={styles.facts}>
            <div><dt className={styles.factTerm}>Applies to</dt><dd className={styles.factValue}>
              {!c.appliesToRoles.length && !c.appliesToDepartments.length ? 'Everyone' : [
                ...c.appliesToRoles.map((r) => (isRole(r) ? ROLE_DISPLAY_NAMES[r] : r)),
                ...c.appliesToDepartments.map(departmentName),
              ].join(', ')}
            </dd></div>
            <div><dt className={styles.factTerm}>Type</dt><dd className={styles.factValue}>{COMPETENCY_TYPE_LABEL[c.competencyType]}</dd></div>
            <div><dt className={styles.factTerm}>Validated by</dt><dd className={`${styles.factValue} ${c.validationMethods.length ? '' : styles.absent}`}>{c.validationMethods.length ? c.validationMethods.map((m) => VALIDATION_METHOD_LABEL[m]).join(', ') : '—'}</dd></div>
            <div><dt className={styles.factTerm}>Renews</dt><dd className={`${styles.factValue} ${c.renewalIntervalDays ? '' : styles.absent}`}>{c.renewalIntervalDays ? `every ${c.renewalIntervalDays} days` : '—'}</dd></div>
            <div><dt className={styles.factTerm}>Published</dt><dd className={`${styles.factValue} ${c.publishedAt ? '' : styles.absent}`}>{formatDay(c.publishedAt)}</dd></div>
            <div><dt className={styles.factTerm}>Last updated</dt><dd className={`${styles.factValue} ${c.updatedAt ? '' : styles.absent}`}>{formatDay(c.updatedAt)}</dd></div>
          </dl>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Description</h2>
          {c.description ? <p className={own.prose}>{c.description}</p> : <p className={`${own.prose} ${own.empty}`}>Not recorded.</p>}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Evidence required</h2>
          <ul className={styles.list}>
            <li className={styles.listItem}><div className={styles.itemName}>Attestation{req.attestation ? '' : ' — not required'}</div>
              {req.attestation && <div className={styles.itemMeta}>{req.attestationText ?? DEFAULT_ATTESTATION}</div>}</li>
            <li className={styles.listItem}><div className={styles.itemName}>File upload{req.fileUpload ? '' : ' — not required'}</div>
              {req.fileUpload && <div className={styles.itemMeta}>A certificate, checklist or other document.</div>}</li>
            <li className={styles.listItem}><div className={styles.itemName}>Manager approval{req.managerApproval ? '' : ' — not required'}</div>
              {req.managerApproval && <div className={styles.itemMeta}>An administrator signs off the evidence before the assignment counts as complete.</div>}</li>
          </ul>
        </section>

        {admin && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Assignments{rows.length ? ` · ${rows.length}` : ''}</h2>
            {assignments.isLoading ? <Spinner label="Loading assignments" /> : rows.length === 0 ? (
              <p className={`${own.prose} ${own.empty}`}>{c.status === 'draft' ? 'Nobody is assigned until it is published.' : 'Nobody has been assigned this yet.'}</p>
            ) : (
              <>
                <p className={own.count} role="status">
                  {(['overdue', 'pending', 'completed', 'waived'] as const).filter((s) => counts[s]).map((s) => `${counts[s]} ${ASSIGNMENT_LABEL[s].toLowerCase()}`).join(' · ')}
                  {pendingApproval.length > 0 && ` · ${pendingApproval.length} awaiting your approval`}
                </p>
                <div className={own.tableWrap}>
                  <table className={own.table}>
                    <thead><tr><th scope="col">Person</th><th scope="col">Status</th><th scope="col">Due</th><th scope="col">Evidence</th><th scope="col"><span className="vh">Actions</span></th></tr></thead>
                    <tbody>
                      {rows.map((a) => {
                        const ev = evidenceByAssignment.get(a.id) ?? (a.evidenceId ? (evidence.data ?? []).find((e) => e.id === a.evidenceId) : undefined);
                        const due = dueText(a);
                        const open = a.status === 'pending' || a.status === 'overdue';
                        return (
                          <AssignmentRow key={a.id} a={a} ev={ev} due={due} open={open} busy={busy}
                            needsApproval={req.managerApproval}
                            waiving={waiving === a.id} onWaive={() => { setWaiving(waiving === a.id ? null : a.id); setDeciding(null); setWaiveReason(''); }}
                            waiveReason={waiveReason} setWaiveReason={setWaiveReason}
                            submitWaive={() => run({ name: 'waiveCompetencyAssignment', assignmentId: a.id, reason: waiveReason }, () => `Waived for ${a.userDisplayName ?? a.userEmail ?? 'that person'}.`)}
                            deciding={ev ? deciding === ev.id : false} onDecide={() => { if (ev) { setDeciding(deciding === ev.id ? null : ev.id); setWaiving(null); setComments(''); } }}
                            comments={comments} setComments={setComments}
                            decide={(approved) => { if (ev) run({ name: 'approveEvidence', evidenceId: ev.id, approved, comments }, () => (approved ? 'Evidence approved; the assignment is complete.' : 'Evidence rejected; the person can submit again.')); }}
                            idPrefix={id(a.id)} />
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Version history</h2>
          {versions.length === 0 ? <p className={`${own.prose} ${own.empty}`}>Not yet published.</p> : (
            <ul className={own.timeline}>{versions.map((v) => (
              <li key={v.id}><span className={own.when}>{formatDay(v.publishedAt)}</span><p className={own.what}>Version {v.versionNumber}</p></li>
            ))}</ul>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          {events.length === 0 ? <p className={`${own.prose} ${own.empty}`}>Nothing recorded yet.</p> : (
            <ul className={own.timeline}>{events.map((e) => (
              <li key={e.id}><span className={own.when}>{formatDay(e.createdAt)}</span>
                <p className={own.what}>{eventLabel(e.action)}{e.actorEmail && <span className={own.who}>{e.actorEmail}</span>}
                  {typeof e.details?.reason === 'string' && <span className={own.note}>{e.details.reason}</span>}</p></li>
            ))}</ul>
          )}
        </section>
      </div>
    </>
  );
}

function AssignmentRow(p: {
  a: Assignment; ev: Evidence | undefined; due: { text: string; cls?: string }; open: boolean; busy: boolean; needsApproval: boolean;
  waiving: boolean; onWaive: () => void; waiveReason: string; setWaiveReason: (v: string) => void; submitWaive: () => void;
  deciding: boolean; onDecide: () => void; comments: string; setComments: (v: string) => void; decide: (approved: boolean) => void;
  idPrefix: string;
}) {
  const { a, ev, due, open, busy } = p;
  const awaiting = open && ev && p.needsApproval && ev.managerApproval === null;
  return (
    <>
      <tr>
        <td>{a.userDisplayName ?? a.userEmail ?? a.userId}{a.userEmail && a.userDisplayName && <span className={own.sub}>{a.userEmail}</span>}</td>
        <td><span className={`${styles.tag} ${styles[ASSIGNMENT_TONE[a.status]]}`}>{ASSIGNMENT_LABEL[a.status]}</span>
          {a.status === 'waived' && a.waivedReason && <span className={own.sub}>{a.waivedReason}</span>}</td>
        <td className={`${own.num} ${due.cls ? own[due.cls] : ''}`}>{due.text}</td>
        <td>
          {ev ? (
            <>
              <span>Submitted {formatDay(ev.submittedAt)}</span>
              {ev.managerApproval && <span className={own.sub}>{ev.managerApproval.approved ? 'Approved' : 'Rejected'}{ev.managerApproval.approvedByEmail ? ` by ${ev.managerApproval.approvedByEmail}` : ''}</span>}
              {awaiting && <span className={own.sub}>Awaiting approval</span>}
            </>
          ) : <span className={own.muted}>—</span>}
        </td>
        <td>
          <div className={own.rowActions}>
            {awaiting && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={p.deciding} onClick={p.onDecide}>Review evidence…</button>}
            {open && <button type="button" className={styles.btn} disabled={busy} aria-expanded={p.waiving} onClick={p.onWaive}>Waive…</button>}
          </div>
        </td>
      </tr>
      {(p.waiving || p.deciding) && (
        <tr>
          <td colSpan={5}>
            {p.waiving && (
              <form className={own.panel} style={{ marginTop: 0 }} onSubmit={(e) => { e.preventDefault(); if (p.waiveReason.trim()) p.submitWaive(); }} aria-labelledby={`${p.idPrefix}-w`}>
                <h3 className={own.panelTitle} id={`${p.idPrefix}-w`}>Waive for {a.userDisplayName ?? a.userEmail}</h3>
                <div className={form.field}>
                  <label className={form.label} htmlFor={`${p.idPrefix}-wr`}>Reason</label>
                  <textarea id={`${p.idPrefix}-wr`} className={form.textarea} rows={2} required maxLength={COMP_REASON_MAX} value={p.waiveReason} onChange={(e) => p.setWaiveReason(e.target.value.slice(0, COMP_REASON_MAX))} />
                  <CharCount value={p.waiveReason} max={COMP_REASON_MAX} />
                </div>
                <div className={styles.actions}>
                  <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !p.waiveReason.trim()}>Waive</button>
                  <button type="button" className={styles.btn} onClick={p.onWaive}>Cancel</button>
                </div>
              </form>
            )}
            {p.deciding && ev && (
              <div className={own.panel} style={{ marginTop: 0 }} role="group" aria-labelledby={`${p.idPrefix}-d`}>
                <h3 className={own.panelTitle} id={`${p.idPrefix}-d`}>Evidence from {ev.userDisplayName ?? ev.userEmail}</h3>
                {ev.attestationText && <p className={own.statement}>{ev.attestationText}</p>}
                {ev.fileUrls.length > 0 && (
                  <ul className={own.files}>{ev.fileUrls.map((u, i) => <li key={u}><a href={u} target="_blank" rel="noreferrer">File {i + 1}</a></li>)}</ul>
                )}
                {ev.notes && <p className={own.prose}>{ev.notes}</p>}
                {(ev.assessorName || ev.assessmentDate || ev.score !== null) && (
                  <p className={own.prose}>
                    {[
                      ev.assessorName ? `Assessed by ${ev.assessorName}` : null,
                      ev.assessmentDate ? `on ${ev.assessmentDate}` : null,
                      ev.score !== null ? `· score ${ev.score}%` : null,
                    ].filter(Boolean).join(' ')}
                  </p>
                )}
                <div className={form.field} style={{ marginTop: 'var(--sp-4)' }}>
                  <label className={form.label} htmlFor={`${p.idPrefix}-c`}>Comments <span className={form.optional}>(optional)</span></label>
                  <textarea id={`${p.idPrefix}-c`} className={form.textarea} rows={2} maxLength={COMP_REASON_MAX} value={p.comments} onChange={(e) => p.setComments(e.target.value.slice(0, COMP_REASON_MAX))} />
                  <CharCount value={p.comments} max={COMP_REASON_MAX} />
                </div>
                <div className={styles.actions}>
                  <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => p.decide(true)}>Approve</button>
                  <button type="button" className={styles.btn} disabled={busy} onClick={() => p.decide(false)}>Reject</button>
                  <button type="button" className={styles.btn} onClick={p.onDecide}>Cancel</button>
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
